<script setup>
import HeadLineChart from '@/components/headline/HeadLineChart.vue'
import LineTable from '@/components/water-line/LineTable.vue'
import MultipleHeadLineTable from '@/components/headline/MultipleHeadLineTable.vue'
import ImportFromPreviousStageDialog from '@/components/schematisationDetail/ImportFromPreviousStageDialog.vue'
import TabTemplate from '@/components/common/TabTemplate.vue'
import ImportDialog from '@/components/common/ImportDialog.vue'
import { computed, inject, onMounted, onUnmounted, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { useHeightComposable } from '@/composables/useHeightComposable'

const HEAD_LINE_NUMBER_LIST = [1, 2]

const store = useStore()

const loading = ref(false)
const hoverPoint = ref({})
const currentMultipleHeadLineState = ref(1)

const headLineChart = ref(null)

const topHeight = inject('topHeight')

const headLineTabsRef = ref(null)
const headLineTabsRefHeight = ref(0)

const { mountObserver, unmountObserver } = useHeightComposable(
  headLineTabsRefHeight,
  headLineTabsRef
)

const multipleHeadline = ref(null)
const multipleHeadLineHeight = ref(0)

const {
  observerWatcher: multipleHeadLineHeightWatcher,
  unmountObserver: unmountMultipleHeadLineObserver
} = useHeightComposable(multipleHeadLineHeight, multipleHeadline)

const reduceHeight = computed(() => {
  return topHeight?.value + headLineTabsRefHeight.value + multipleHeadLineHeight.value + 'px'
})

const currentStabSchematisationStage = computed(
  () => store.state.stabSchematisationStage.currentItem
)
const currentMultipleHeadLine = computed(() => store.state.multipleHeadLine.currentItem)
const importLoading = computed(() => store.state.multipleHeadLine.importLoading)
const importDialog = computed(() => store.state.multipleHeadLine.importDialog)

const importFromHydraulicHeadDialog = computed(
  () => store.state.headLine.importFromHydraulicHeadDialog
)
const importFromHydraulicHeadLoading = computed(
  () => store.state.headLine.importFromHydraulicHeadLoading
)
const headLine2 = computed(() => store.getters['multipleHeadLine/getHeadLine2State'])
const schematisationStagesToImport = computed(
  () => store.getters['stabSchematisationStage/previousSchematisationStagesHaveHeadLine']
)
const isLockedHeadLine2 = computed(() => store.getters['stabSchematisationStage/isLockedHeadLine2'])
const isLocked = computed(() => store.getters['stabSchematisation/isLockedCurrent'])
const headLine2Switcher = computed({
  get: () => headLine2.value,
  set: async (value) => await changeHeadLine2State(value)
})

watch(currentStabSchematisationStage, async () => {
  currentMultipleHeadLineState.value = 1
  hoverPoint.value = {}
  deleteActiveItem()
  if (currentStabSchematisationStage.value) {
    await resetState()
    await fetchItems()
  }
})
watch(currentMultipleHeadLine, multipleHeadLineHeightWatcher, { deep: true })

const setDirtyObject = () => store.dispatch('multipleHeadLine/setDirtyObject')
const fetchMultipleHeadLineItems = async (id) => store.dispatch('multipleHeadLine/fetchItems', id)
const setCurrentMultipleHeadLine = (item) => store.dispatch('multipleHeadLine/setCurrentItem', item)
const toggleImportDialog = (value) => store.dispatch('multipleHeadLine/toggleImportDialog', value)
const setNestedParameter = (payload) =>
  store.dispatch('multipleHeadLine/setNestedParameter', payload)
const updateHeadLine2 = (enabled) => store.dispatch('multipleHeadLine/updateHeadLine2', enabled)
const importHeadLineFromStage = (payload) =>
  store.dispatch('multipleHeadLine/importHeadLineFromStage', payload)

const emptyDirtyItems = () => store.dispatch('stageConfiguration/emptyDirtyItems')

const fetchHeadLineItems = (payload) => store.dispatch('headLine/fetchItems', payload)
const generateHeadLine = () => store.dispatch('headLine/generate')
const resetHeadLineState = () => store.dispatch('headLine/resetState')
const importHeadLineFromHydraulicHead = () => store.dispatch('headLine/importFromHydraulicHead')
const toggleImportFromHydraulicHeadDialog = () =>
  store.dispatch('headLine/toggleImportFromHydraulicHeadDialog')

const fetchHeadReferenceLineItems = (payload) =>
  store.dispatch('headReferenceLine/fetchItems', payload)
const generateHeadReferenceLine = () => store.dispatch('headReferenceLine/generate')

const resetHeadReferenceLineState = () => store.dispatch('headReferenceLine/resetState')

const resetState = async () => {
  await resetHeadLineState()
  await resetHeadReferenceLineState()
}

watch(currentMultipleHeadLineState, async (value) => {
  if (value === 2 && !headLine2.value && !isLocked.value) {
    await changeHeadLine2State(true)
  }
  hoverPoint.value = {}
  deleteActiveItem()
  await setCurrentMultipleHeadLine(getMultipleHeadLineItem(value))
  await setCurrentState()
  setDirtyObject()
})

const fetchLine = async (stage) => {
  if (currentMultipleHeadLine.value) {
    await fetchHeadLineItems({
      id: stage,
      currentId: currentMultipleHeadLine.value['headline']
    })
    await fetchHeadReferenceLineItems({
      id: stage,
      currentId: currentMultipleHeadLine.value['headreferenceline']
    })
  }
}
const fetchItems = async () => {
  await fetchMultipleHeadLineItems(currentStabSchematisationStage.value['id'])
  await fetchLine(currentStabSchematisationStage.value['id'])
}

const generateHeadLineHandler = async () => {
  const isGenerated = await generateHeadLine()
  if (isGenerated && multipleHeadline.value) {
    multipleHeadline.value[currentMultipleHeadLineState.value - 1].dirtyItems = []
  }
}

const setCurrentState = async () => {
  let storeNamespaceList = {
    headLine: 'headline',
    headReferenceLine: 'headreferenceline'
  }
  Object.keys(storeNamespaceList).forEach((storeNamespace) => {
    let items = store.state[storeNamespace]['items']
    if (items.length) {
      store.dispatch(
        storeNamespace + '/setCurrentItem',
        items[
          getIndexItem(items, currentMultipleHeadLine.value[storeNamespaceList[storeNamespace]])
        ]
      )
    }
  })
}
const changeHeadLine2State = async (value) => {
  const multipleHeadLine2 = getMultipleHeadLineItem(2)
  try {
    await setNestedParameter({
      value: value,
      item: multipleHeadLine2,
      parameter: 'enabled'
    })
    await updateHeadLine2(value)
    if (value === false) {
      currentMultipleHeadLineState.value = 1
      await setCurrentMultipleHeadLine(getMultipleHeadLineItem(1))
      await setCurrentState()
    }
    emptyDirtyItems()
  } catch (e) {
    await setNestedParameter({
      value: !value,
      item: multipleHeadLine2,
      parameter: 'enabled'
    })
    currentMultipleHeadLineState.value = 1
  }
}
const multipleHeadLineItems = computed(() => store.state.multipleHeadLine.items)
const getMultipleHeadLineItem = (number) => {
  return multipleHeadLineItems.value.find((item) => {
    return item.number == number
  })
}
const getIndexItem = (items, multipleLine) => {
  return items.findIndex((item) => {
    return item.id === multipleLine
  })
}
const setActiveItem = (index, storeNamespace) => {
  headLineChart.value.waterLineChart.setActiveItem({
    storeNamespace: storeNamespace,
    index: index
  })
}
const deleteActiveItem = () => {
  headLineChart.value.waterLineChart.deleteActiveItem()
}
const getHoverPoint = (point) => {
  hoverPoint.value = point
}
const handleImportFromHydraulicHead = async () => {
  try {
    await importHeadLineFromHydraulicHead()
  } catch (e) {
    console.log(e)
  } finally {
    toggleImportFromHydraulicHeadDialog()
  }
}
const handleImport = async (sourceSchematisationStageId) => {
  try {
    deleteActiveItem()
    await importHeadLineFromStage({
      sourceSchematisationStageId,
      targetSchematisationStageId: currentStabSchematisationStage.value.id,
      headLineNumber: currentMultipleHeadLine.value.number
    })
  } catch (e) {
    console.log(e)
  } finally {
    toggleImportDialog(false)
  }
}
onMounted(async () => {
  loading.value = true
  await mountObserver()
  await resetState()
  try {
    await fetchItems()
  } catch (e) {
    console.log(e)
  }
  loading.value = false
})

onUnmounted(() => {
  unmountObserver()
  unmountMultipleHeadLineObserver()
})
</script>
<template>
  <TabTemplate>
    <template v-slot:chart>
      <HeadLineChart ref="headLineChart" @clickPoint="getHoverPoint"></HeadLineChart>
    </template>
    <template v-slot:content>
      <v-tabs color="primary" grow v-model="currentMultipleHeadLineState">
        <v-tab width="50%" ref="headLineTabsRef" color="primary" :value="1"> Head Line 1</v-tab>
        <v-tab
          :disabled="(!headLine2 && isLocked) || isLockedHeadLine2"
          :value="2"
          color="primary"
          width="50%"
        >
          Head Line 2
          <template #append>
            <v-switch
              color="primary"
              :class="[isLocked ? 'disable-events' : '']"
              :disabled="isLocked || isLockedHeadLine2"
              v-model="headLine2Switcher"
              @click.stop=""
              hide-details
            ></v-switch>
          </template>
        </v-tab>
      </v-tabs>
      <v-window v-model="currentMultipleHeadLineState">
        <v-window-item v-for="headline in HEAD_LINE_NUMBER_LIST" :key="headline" :value="headline">
          <v-lazy>
            <div class="d-flex fill-height flex-column">
              <MultipleHeadLineTable
                v-if="currentMultipleHeadLine"
                :key="'multipleHeadline' + headline"
                ref="multipleHeadline"
                :number="headline"
                class="py-2"
              />
              <v-row no-gutters :key="'headlineTable' + headline">
                <v-col cols="6" :loading="loading" class="pa-0 fill-height">
                  <LineTable
                    text="Hydraulic Head Line"
                    storeNamespace="headLine"
                    :hoverPoint="hoverPoint"
                    :generateButton="true"
                    :readonly="isLocked"
                    :reduce-height="reduceHeight"
                    import-button
                    @setActiveItem="setActiveItem($event, 'headLine')"
                    @generate="generateHeadLineHandler()"
                    @import="toggleImportFromHydraulicHeadDialog"
                  />
                </v-col>
                <v-col cols="6" :loading="loading" class="pa-0 fill-height">
                  <LineTable
                    text="Head Reference Line"
                    storeNamespace="headReferenceLine"
                    :hoverPoint="hoverPoint"
                    :reduce-height="reduceHeight"
                    :generateButton="true"
                    :readonly="isLocked"
                    @setActiveItem="setActiveItem($event, 'headReferenceLine')"
                    @generate="generateHeadReferenceLine"
                  />
                </v-col>
              </v-row>
            </div>
          </v-lazy>
        </v-window-item>
      </v-window>
      <ImportFromPreviousStageDialog
        :schematisation-stages="schematisationStagesToImport"
        v-model="importDialog"
        :loading="importLoading"
        text="head lines"
        @close="toggleImportDialog(false)"
        @import="handleImport"
      />
      <ImportDialog
        :dialog="importFromHydraulicHeadDialog"
        :loading="importFromHydraulicHeadLoading"
        text="hydraulic head line"
        @close="toggleImportFromHydraulicHeadDialog"
        @import="handleImportFromHydraulicHead"
      />
    </template>
  </TabTemplate>
</template>
